import { useState, useEffect, useRef, useCallback } from 'react';

interface SpeechBubbleProps {
  text: string;
  isVisible: boolean;
  typingSpeedMs?: number;
  autoHide?: boolean;
  onHide?: () => void;
}

export function SpeechBubble({ 
  text, 
  isVisible, 
  typingSpeedMs = 14,
  autoHide = true,
  onHide 
}: SpeechBubbleProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [state, setState] = useState<'hidden' | 'visible'>('hidden');
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const runIdRef = useRef(0);

  const HIDE_CLEAR_DELAY_MS = 360;
  const AUTO_HIDE_EXTRA_MS = 2000;

  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false 
    : false;

  const hide = useCallback(() => {
    runIdRef.current += 1;
    setState('hidden');
    
    setTimeout(() => {
      setDisplayedText('');
    }, HIDE_CLEAR_DELAY_MS);

    onHide?.();
  }, [onHide]);

  const showAndType = useCallback(() => {
    runIdRef.current += 1;
    const thisRun = runIdRef.current;

    if (textRef.current) {
      textRef.current.textContent = text;
      const measuredHeight = Math.ceil(textRef.current.getBoundingClientRect().height);
      if (measuredHeight > 0) {
        setContentHeight(measuredHeight);
      }
      textRef.current.textContent = '';
    }

    setState('visible');

    if (autoHide) {
      const typingDuration = text.length * typingSpeedMs;
      const totalDuration = typingDuration + AUTO_HIDE_EXTRA_MS;
      
      setTimeout(() => {
        if (thisRun === runIdRef.current) {
          hide();
        }
      }, totalDuration);
    }

    if (prefersReducedMotion) {
      setDisplayedText(text);
      return;
    }

    let index = 0;
    const tick = () => {
      if (thisRun !== runIdRef.current) return;
      setDisplayedText(text.slice(0, index));
      if (index >= text.length) return;
      index += 1;
      setTimeout(tick, typingSpeedMs);
    };

    requestAnimationFrame(tick);
  }, [text, typingSpeedMs, prefersReducedMotion, hide, autoHide]);

  useEffect(() => {
    if (isVisible) {
      showAndType();
    } else {
      hide();
    }
  }, [isVisible, showAndType, hide]);

  return (
    <div 
      className="speech-bubble"
      data-state={state}
      style={contentHeight ? { '--bubble-content-height': `${contentHeight}px` } as React.CSSProperties : undefined}
    >
      <span className="sr-only">{text}</span>
      <span 
        ref={textRef}
        className="speech-bubble__text" 
        aria-hidden="true"
      >
        {displayedText}
      </span>
    </div>
  );
}
